/**
 * RosterScheduleCard — read-only inline rendering of a shift roster: the
 * nurse × day board of what the ward is actually rostered for. The editable
 * board lives in apps/roster (RosterBoard); this is the light read-only twin
 * shown inline in the live conversation + the agent-representations preview.
 * Built from RosterEntry[] — the projection of a page of `GET /shifts`
 * (rosterEntries). A shift renders as its code.
 */
import { useTranslation } from 'react-i18next'
import { ShiftChip } from './ShiftChip'
import type { RosterEntry } from './rosterEntries'

interface RosterScheduleCardProps {
  entries: RosterEntry[]
  /** Optional unit / week label shown above the grid. */
  unitLabel?: string
}

export function RosterScheduleCard({ entries, unitLabel }: RosterScheduleCardProps) {
  const { t, i18n } = useTranslation()
  if (entries.length === 0) return null

  const dates = [...new Set(entries.map((e) => e.date))].sort()
  const nurses = [...new Set(entries.map((e) => e.nurse))].sort((a, b) => a.localeCompare(b))
  const byCell = new Map<string, RosterEntry>()
  for (const e of entries) byCell.set(`${e.nurse}|${e.date}`, e)

  const dayLabel = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`)
    if (Number.isNaN(d.getTime())) return iso
    const wd = d.toLocaleDateString(i18n.language, { weekday: 'short' })
    const dm = d.toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit' })
    return `${wd} ${dm}`
  }

  return (
    <div className="overflow-hidden rounded-[14px] border border-neutral-200 bg-white">
      {unitLabel && (
        <div className="border-b border-neutral-200 px-4 py-2.5 text-[11.5px] font-semibold text-neutral-500">
          {unitLabel}
        </div>
      )}

      {/* Nurse × day grid */}
      <div className="overflow-x-auto p-3">
        <table className="w-full min-w-[480px] border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="px-2 pb-2 text-left text-[12px] font-semibold text-neutral-700">
                {t('roster.nurseColumn', { defaultValue: 'Pflege' })}
              </th>
              {dates.map((d) => (
                <th key={d} className="px-2 pb-2 text-center text-[12px] font-semibold text-neutral-700">
                  {dayLabel(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nurses.map((nurse) => (
              <tr key={nurse}>
                <td className="border-t border-neutral-200 px-2 py-2 text-[12.5px] font-semibold text-neutral-800">
                  {nurse}
                </td>
                {dates.map((d) => {
                  const entry = byCell.get(`${nurse}|${d}`)
                  return (
                    <td key={d} className="border-t border-neutral-200 px-2 py-2 text-center">
                      {entry ? (
                        <ShiftChip code={entry.shift} display={entry.display} />
                      ) : (
                        <span className="text-neutral-300" aria-hidden="true">
                          ·
                        </span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
